import apiClient from '../client';
import type {
  Patient,
  PatientDetail,
  PatientProblem,
  PatientMedication,
  PatientAllergy,
  PatientFilters,
  CreatePatientRequest,
  UpdatePatientRequest,
  CreateProblemRequest,
  UpdateProblemRequest,
  CreateMedicationRequest,
  UpdateMedicationRequest,
  SuspendMedicationRequest,
  ReactivateMedicationRequest,
  CreateAllergyRequest,
  UpdateAllergyRequest,
  TimelineEvent,
  InactivatePatientRequest,
  MarkDeceasedRequest,
  DischargePatientRequest,
  InactivationStats,
  InactivationReason,
  ProgramStatusCounts,
} from '@/types/patient.types';
import type { PaginatedResponse, PaginationParams } from '@/api/types/pagination.types';

export const patientsApi = {
  // Patient CRUD
  getAll: async (
    params: PaginationParams & PatientFilters
  ): Promise<PaginatedResponse<Patient>> => {
    const response = await apiClient.get<PaginatedResponse<Patient>>('/v1/patients', { params });
    return response.data;
  },

  search: async (query: string, limit: number = 10): Promise<Patient[]> => {
    const response = await apiClient.get<Patient[]>('/v1/patients/search', {
      params: { query, limit },
    });
    return response.data;
  },

  getById: async (id: string): Promise<PatientDetail> => {
    const response = await apiClient.get<PatientDetail>(`/v1/patients/${id}`);
    return response.data;
  },

  getByCpf: async (cpf: string): Promise<PatientDetail> => {
    const response = await apiClient.get<PatientDetail>(`/v1/patients/cpf/${cpf}`);
    return response.data;
  },

  create: async (data: CreatePatientRequest): Promise<PatientDetail> => {
    const response = await apiClient.post<PatientDetail>('/v1/patients', data);
    return response.data;
  },

  update: async (id: string, data: UpdatePatientRequest): Promise<PatientDetail> => {
    const response = await apiClient.put<PatientDetail>(`/v1/patients/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/v1/patients/${id}`);
  },

  // Timeline
  getTimeline: async (
    id: string,
    params?: { startDate?: string; endDate?: string }
  ): Promise<TimelineEvent[]> => {
    const response = await apiClient.get<TimelineEvent[]>(`/v1/timeline/patient/${id}`, {
      params,
    });
    return response.data;
  },

  // Problems
  addProblem: async (patientId: string, data: CreateProblemRequest): Promise<PatientProblem> => {
    const response = await apiClient.post<PatientProblem>(
      `/v1/patients/${patientId}/problems`,
      data
    );
    return response.data;
  },

  updateProblem: async (
    patientId: string,
    problemId: string,
    data: UpdateProblemRequest
  ): Promise<PatientProblem> => {
    const response = await apiClient.put<PatientProblem>(
      `/v1/patients/${patientId}/problems/${problemId}`,
      data
    );
    return response.data;
  },

  deleteProblem: async (patientId: string, problemId: string): Promise<void> => {
    await apiClient.delete(`/v1/patients/${patientId}/problems/${problemId}`);
  },

  // Medications
  addMedication: async (
    patientId: string,
    data: CreateMedicationRequest
  ): Promise<PatientMedication> => {
    const response = await apiClient.post<PatientMedication>(
      `/v1/patients/${patientId}/medications`,
      data
    );
    return response.data;
  },

  updateMedication: async (
    patientId: string,
    medicationId: string,
    data: UpdateMedicationRequest
  ): Promise<PatientMedication> => {
    const response = await apiClient.put<PatientMedication>(
      `/v1/patients/${patientId}/medications/${medicationId}`,
      data
    );
    return response.data;
  },

  deleteMedication: async (patientId: string, medicationId: string): Promise<void> => {
    await apiClient.delete(`/v1/patients/${patientId}/medications/${medicationId}`);
  },

  suspendMedication: async (
    patientId: string,
    medicationId: string,
    data: SuspendMedicationRequest
  ): Promise<PatientMedication> => {
    const response = await apiClient.post<PatientMedication>(
      `/v1/patients/${patientId}/medications/${medicationId}/suspend`,
      data
    );
    return response.data;
  },

  reactivateMedication: async (
    patientId: string,
    medicationId: string,
    data: ReactivateMedicationRequest
  ): Promise<PatientMedication> => {
    const response = await apiClient.post<PatientMedication>(
      `/v1/patients/${patientId}/medications/${medicationId}/reactivate`,
      data
    );
    return response.data;
  },

  // Allergies
  addAllergy: async (patientId: string, data: CreateAllergyRequest): Promise<PatientAllergy> => {
    const response = await apiClient.post<PatientAllergy>(
      `/v1/patients/${patientId}/allergies`,
      data
    );
    return response.data;
  },

  updateAllergy: async (
    patientId: string,
    allergyId: string,
    data: UpdateAllergyRequest
  ): Promise<PatientAllergy> => {
    const response = await apiClient.put<PatientAllergy>(
      `/v1/patients/${patientId}/allergies/${allergyId}`,
      data
    );
    return response.data;
  },

  deleteAllergy: async (patientId: string, allergyId: string): Promise<void> => {
    await apiClient.delete(`/v1/patients/${patientId}/allergies/${allergyId}`);
  },

  // Inactivation
  inactivate: async (
    patientId: string,
    data: InactivatePatientRequest
  ): Promise<PatientDetail> => {
    const response = await apiClient.post<PatientDetail>(
      `/v1/patients/${patientId}/inactivate`,
      data
    );
    return response.data;
  },

  markDeceased: async (
    patientId: string,
    data: MarkDeceasedRequest
  ): Promise<PatientDetail> => {
    const response = await apiClient.post<PatientDetail>(
      `/v1/patients/${patientId}/mark-deceased`,
      data
    );
    return response.data;
  },

  reactivate: async (patientId: string): Promise<PatientDetail> => {
    const response = await apiClient.post<PatientDetail>(
      `/v1/patients/${patientId}/reactivate`
    );
    return response.data;
  },

  discharge: async (
    patientId: string,
    data: DischargePatientRequest
  ): Promise<PatientDetail> => {
    const response = await apiClient.post<PatientDetail>(
      `/v1/patients/${patientId}/discharge`,
      data
    );
    return response.data;
  },

  getInactivated: async (
    params: PaginationParams & { reason?: InactivationReason; deceased?: boolean }
  ): Promise<PaginatedResponse<Patient>> => {
    const response = await apiClient.get<PaginatedResponse<Patient>>(
      '/v1/patients/inactivated',
      { params }
    );
    return response.data;
  },

  getInactivationStats: async (): Promise<InactivationStats> => {
    const response = await apiClient.get<InactivationStats>(
      '/v1/patients/inactivation-stats'
    );
    return response.data;
  },

  getStatusCounts: async (): Promise<ProgramStatusCounts> => {
    const response = await apiClient.get<ProgramStatusCounts>(
      '/v1/patients/status-counts'
    );
    return response.data;
  },
};
